// View-as-Customer state (A3.1b — locked in doc/02 §4.2.1).
//
// Holds the accountant's "view as" target across page navigations.
// Persisted in sessionStorage so a refresh keeps the mode; cleared on logout.
//
// Axios interceptor (lib/api.ts) reads this and appends `?as_customer={id}` to
// customer-facing endpoints (`/invoices`, `/credit-notes`, `/ar/*`,
// `/customer/dashboard`). Admin/accountant endpoints are unaffected.

const STORAGE_KEY = "finapp.view-as";
const EVENT_NAME = "finapp:view-as-changed";

export interface ViewAsTarget {
  user_id: number;
  display_name: string;
}

const ENDPOINT_PREFIXES = [
  "/invoices",
  "/credit-notes",
  "/ar/",
  "/customer/",
];

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function getViewAsCustomer(): ViewAsTarget | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ViewAsTarget;
    if (typeof parsed?.user_id !== "number" || typeof parsed?.display_name !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setViewAsCustomer(target: ViewAsTarget | null): void {
  if (!isBrowser()) return;
  if (target) {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
  } else {
    window.sessionStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: target }));
}

export function clearViewAsCustomer(): void { setViewAsCustomer(null); }

export function subscribeViewAsCustomer(cb: (target: ViewAsTarget | null) => void): () => void {
  if (!isBrowser()) return () => {};
  const handler = (e: Event) => cb((e as CustomEvent).detail || null);
  const storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb(getViewAsCustomer());
  };
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener("storage", storageHandler);
  };
}

/** Should this URL (relative to API_BASE) be augmented with `?as_customer=`? */
export function shouldInjectAsCustomer(url: string): boolean {
  // Strip query string for prefix check.
  const path = url.split("?")[0];
  return ENDPOINT_PREFIXES.some((p) => path.startsWith(p));
}
